<template>
    <div class="white-box">
        <transition
            name="opacity"
            mode="out-in"
        >
            <Skeletons v-if="isGettingData" />
            <div v-else>
                <h4 class="pb-2.5 md:pb-5">
                    請選擇您欲轉出之投資標的
                </h4>
                <div
                    v-if="!!msgList.length"
                    class="pt-0.5 text-warning text-sm xs:text-base mb-2.5"
                >
                    <p
                        v-for="(msgStr,idx) in msgList"
                        :key="idx"
                        class="mb-1"
                    >
                        {{ msgStr }}
                    </p>
                </div>
                <div
                    v-for="(target,idx) in data"
                    :key="idx"
                    class="border-t last:border-b border-gray-200"
                >
                    <CollapseTarget
                        v-model:field="data[idx]"
                        :idx="idx"
                        :currencyData="{currencyText:policy.currencyText,currency:policy.currency}"
                        needTargetCurrency
                        :redeemAmountRule="policy.currency === 'NTD' ? 'round' : 'float'"
                        :floatNum="policy.currency === 'NTD' ? 0 : 2"
                        openButtonName="轉出"
                        foldable
                    >
                        <template #data>
                            <!-- 手機版 -->
                            <div class="md:hidden">
                                <div class="grid grid-cols-3 gap-x-1.5 pr-7 pb-2.5 border-b border-dashed border-gray-200">
                                    <div class="col-span-2 pr-1">
                                        <ContentAndTitle
                                            :title="`預估帳戶價值(${policy.currencyText})`"
                                            :content="target.acnoValueDisplay"
                                        />
                                    </div>
                                    <div class="col-span-1 w-20">
                                        <div class="text-right">
                                            <ContentAndTitle title="報酬率">
                                                <template #content>
                                                    <span :class="[target.returnRate < 0 ?'text-green-700':'text-[#DE0000]']">
                                                        {{ target.returnRate }}%
                                                    </span>
                                                </template>
                                            </ContentAndTitle>
                                        </div>
                                    </div>
                                </div>
                                <transition name="expand">
                                    <div v-show="!target.isFold">
                                        <div class="grid grid-cols-3 gap-x-1.5 pr-7 pt-2.5">
                                            <div class="col-span-1">
                                                <ContentAndTitle
                                                    title="最新淨值"
                                                    :content="target.priceUsedDisplay"
                                                />
                                            </div>
                                            <div class="col-span-1">
                                                <ContentAndTitle
                                                    title="最新匯率"
                                                    :content="target.echgBrtDisplay"
                                                />
                                            </div>
                                            <div class="col-span-1">
                                                <ContentAndTitle
                                                    title="淨值匯率日期"
                                                    :content="target.exchangeRateDate"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </transition>
                                <div class="flex justify-between items-end my-2.5">
                                    <div>
                                        <div class="col-span-1">
                                            <ContentAndTitle
                                                title="持有單位數"
                                                :content="target.unitString"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- 手機版END -->
                            <!-- 電腦版 -->
                            <div class="hidden md:block">
                                <div class="grid grid-cols-12 lg:grid-cols-8 gap-1 my-2.5 pr-[3.75rem]">
                                    <div class="col-span-6 lg:col-span-2">
                                        <ContentAndTitle
                                            :title="`預估帳戶價值(${policy.currencyText})`"
                                            :content="target.acnoValueDisplay"
                                        />
                                    </div>
                                    <div class="col-span-6 lg:col-span-2">
                                        <ContentAndTitle
                                            title="持有單位數"
                                            :content="target.unitString"
                                        />
                                    </div>
                                    <div class="col-span-3  lg:col-span-1">
                                        <ContentAndTitle
                                            title="最新淨值"
                                            :content="target.priceUsedDisplay"
                                        />
                                    </div>
                                    <div class="col-span-3  lg:col-span-1">
                                        <ContentAndTitle
                                            title="最新匯率"
                                            :content="target.echgBrtDisplay"
                                        />
                                    </div>
                                    <div class="col-span-3  lg:col-span-1">
                                        <ContentAndTitle
                                            title="淨值匯率日期"
                                            :content="target.exchangeRateDate"
                                        />
                                    </div>
                                    <div class="col-span-3  lg:col-span-1">
                                        <ContentAndTitle
                                            title="報酬率"
                                            :content="target.acnoValueDisplay"
                                        >
                                            <template #content>
                                                <span :class="{'text-green-700':target.returnRate < 0 ,'text-[#DE0000]':target.returnRate > 0}">
                                                    {{ target.returnRate }}%
                                                </span>
                                            </template>
                                        </ContentAndTitle>
                                    </div>
                                </div>
                            </div>
                            <!-- 電腦版END -->
                        </template>
                    </CollapseTarget>
                </div>
            </div>
        </transition>
    </div>
    <Bottombar>
        <template #top>
            <div class="text-xs py-2.5">
                預估轉出總金額({{ policy.currencyText }})：
            </div>
            <div class="text-lg py-2.5">
                {{ addComma(transformSum) }}
            </div>
        </template>
        <template #center>
            <div class="text-base py-2.5">
                預估轉出總金額({{ policy.currencyText }})： {{ addComma(transformSum) }}
            </div>
        </template>
        <template #right>
            <button
                class="btn-text arrow-r"
                :disabled="!data.some(i => i.isRedeem)"
                type="button"
                @click="validateData()"
            >
                選擇轉入標的
            </button>
        </template>
    </Bottombar>
    <Loading :is-loading="isLoading" />
</template>

<script>
import { ref, computed, inject } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { getInfoA6 } from '@/apis/A6.js';
import { useStore } from 'vuex';
import { addComma } from '@/utils/number/addComma';
import moment from 'moment';
import CollapseTarget from '@/components/collapse/CollapseTarget.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import routeName from '@/router/constant.js';
import Loading from '@/components/Loading.vue';
import ContentAndTitle from '@/components/text/ContentAndTitle.vue';
import Decimal from 'decimal.js';
import dialogContnet from '@/json/dialogContent.js';
import Skeletons from './Skeletons.vue';
import _ from 'lodash';

export default {
    components: {
        CollapseTarget,
        Bottombar,
        Loading,
        ContentAndTitle,
        Skeletons,
    },
    setup() {
        const $vfm = inject('$vfm');
        const router = useRouter();
        const route = useRoute();
        const store = useStore();
        const { code } = route.meta;
        const isLoading = ref(false);
        const isGettingData = ref(false);
        const isLoadingData = ref(false);
        const transformOptions = ref('UNIT');
        const data = ref([]);
        const msgList = ref([]);
        /* 保單資料 */
        const policy = computed(() => store.state.policy.policy);
        const result = computed(() => {
            const vuexData = store.state.services.changeItemsResult[`${code}EXPORT`];
            return vuexData ? _.cloneDeep(vuexData) : undefined;
        });

        const getData = () => {
            isGettingData.value = true;
            getInfoA6().then((res) => {
                if (res.data) {
                    msgList.value = res.data.msgList;
                    data.value = res.data.transformList.map(i => {
                        i.acnoValueDisplay = addComma(i.acnoValue);
                        i.priceUsedDisplay = addComma(i.priceUsed);
                        i.echgBrtDisplay = addComma(i.echgBrt);
                        // 是否執行轉出or贖回，預設都不要
                        i.isRedeem = false;
                        // 轉出or贖回形式 UNIT or PERCENT，預設依單位數
                        i.redeemBy = 'UNIT';
                        // 轉出or贖回單位數
                        i.redeemUnit = 0;
                        // 轉出or贖回比例，正整數
                        i.redeemPercent = 0;
                        // 預估轉入金額
                        i.redeemAmount = 0;
                        // 是否折疊，預設要折
                        i.isFold = true;
                        // 匯率日期
                        if (i.exchangeRateDate) i.exchangeRateDate = moment(i.exchangeRateDate).format('YYYY/MM/DD');
                        // vuex 有資料
                        if (result.value) {
                            result.value.transformOutList.forEach(j => {
                                // mapping資料
                                if (i.fundCode === j.fundCode) {
                                    // 依比例或依單位數有值
                                    if (!!j.redeemUnit || !!j.redeemPercent) {
                                        i.isRedeem = true;
                                        i.redeemBy = j.redeemUnit ? 'UNIT' : 'PERCENT';
                                        i.redeemUnit = j.redeemUnit ? j.redeemUnit : 0;
                                        i.redeemPercent = j.redeemPercent ? j.redeemPercent : 0;
                                    }
                                }
                            });
                        }
                        return i;
                    });
                }
            }).finally(() => {
                isGettingData.value = false;
            });
        };
        getData();

        // 轉出金額總和
        const transformSum = computed(() => {
            let sum = new Decimal(0);
            data.value.forEach(i => {
                sum = sum.plus(i.redeemAmount);
            });
            return parseFloat(sum);
        });
        const validateData = () => {
            isLoading.value = true;
            data.value.forEach(i => {
                // 如果要轉出
                if (i.isRedeem) {
                    // 如果選擇單位數
                    if (i.redeemBy === 'UNIT' && !i.redeemUnit) i.errorMsgRedeemUnit = '請輸入數值';
                    // 如果選擇比例
                    if (i.redeemBy === 'PERCENT' && !i.redeemPercent) i.errorMsgRedeemPercent = '請輸入介於1~100之數字';
                }
            });

            const hasError = data.value.some(i => !!i.errorMsgRedeemUnit || !!i.errorMsgRedeemPercent);
            if (hasError) {
                modalShow(dialogContnet.validateMsg);
                isLoading.value = false;
            } else if (transformSum.value === 0) {
                modalShow({
                    title: '提示',
                    content: '預估轉出金額需大於0。',
                    confirmButton: '確認',
                });
                isLoading.value = false;
            } else {
                goNext();
            }
        };

        const goNext = () => {
            const returnData = {};
            const tempArr = [];
            data.value.forEach(i => {
                if (i.isRedeem) {
                    const obj = {};
                    obj.fundCode = i.fundCode;
                    obj.redeemUnit = i.redeemUnit;
                    obj.redeemPercent = i.redeemPercent;
                    if (!i.disable) tempArr.push(obj);
                }
            });
            returnData.transformOutList = tempArr;
            store.dispatch('services/setChangeItemsResult', { code: `${code}EXPORT`, value: returnData });
            router.push({ name: routeName.TRANSFORM_IMPORT });
            isLoading.value = false;
        };

        const modalShow = (params) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                    },
                    closed() {
                        $vfm.hide('DialogBase');
                    }
                },
            });
        };
        return {
            data,
            policy,
            isLoadingData,
            transformSum,
            isLoading,
            isGettingData,
            transformOptions,
            msgList,
            addComma,
            validateData,
            goNext
        };
    }
};
</script>
<style lang="scss" scoped>
.expand-leave-active,
.expand-enter-active {
    transition: max-height 0.3s linear;
}
.expand-enter-from,
.expand-leave-to {
    max-height: 0;
    overflow: hidden;
}

.expand-enter-to,
.expand-leave-from {
    max-height: 100px;
    overflow: hidden;
}
</style>
